import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PRODUCTS } from "@/data/products";
import { waProduct } from "@/lib/whatsapp";
import { ContactCTA } from "@/components/site/ContactCTA";

const categories = ["All", "Interior", "Exterior", "Wood & Metal"];

export default function ProductsPage() {
    const [cat, setCat] = useState("All");
    const items = useMemo(
        () => (cat === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === cat)),
        [cat],
    );

    return (
        <main className="bg-bone text-ink" data-testid="page-products">
            {/* Page hero */}
            <section className="pt-32 md:pt-40 pb-16 md:pb-24 border-b border-ink/10">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50">
                        Home · 02 / Products
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-4 font-display text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-[-0.02em]"
                    >
                        Every finish.
                        <br />
                        <span className="italic text-brand-red">One house.</span>
                    </motion.h1>
                    <p className="mt-6 max-w-xl text-ink/70 text-lg">
                        Fifteen precision-engineered coatings — from the primer that grips first, to
                        the varnish that seals last. Tap any product to enquire on WhatsApp.
                    </p>

                    <div className="mt-10 flex flex-wrap gap-2">
                        {categories.map((c) => (
                            <button
                                key={c}
                                onClick={() => setCat(c)}
                                className={`btn-pill border ${
                                    cat === c ? "bg-ink text-bone border-ink" : "border-ink/20 hover:border-ink/60"
                                }`}
                                data-testid={`filter-${c.replace(/\s|&/g, "-").toLowerCase()}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Product list — editorial two-column */}
            <section className="py-16 md:py-24">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                    <div className="grid gap-4">
                        {items.map((p, i) => (
                            <motion.a
                                key={p.id}
                                href={waProduct(p.name)}
                                target="_blank"
                                rel="noreferrer"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.7, delay: (i % 4) * 0.06 }}
                                className="group grid grid-cols-12 items-center gap-4 border-t border-ink/15 py-6 md:py-8 hover:bg-cream/50 transition-colors -mx-3 px-3"
                                data-testid={`product-row-${p.id}`}
                            >
                                <div className="col-span-2 md:col-span-1 flex items-center gap-3">
                                    <span
                                        className="block w-4 h-4 rounded-full ring-1 ring-ink/20"
                                        style={{ background: p.accent }}
                                    />
                                    <span className="font-mono text-[10px] tracking-widest text-ink/50 hidden md:inline">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <div className="col-span-10 md:col-span-5">
                                    <div className="font-display text-2xl md:text-4xl leading-tight">
                                        {p.name}
                                    </div>
                                    <div className="mt-1 font-mono text-[10px] tracking-widest text-ink/40 uppercase">
                                        {p.category}
                                    </div>
                                </div>
                                <div className="col-span-12 md:col-span-4 text-ink/70 text-sm md:text-base">
                                    {p.blurb}
                                </div>
                                <div className="col-span-12 md:col-span-2 flex md:justify-end">
                                    <span className="inline-flex items-center gap-2 text-sm text-ink group-hover:text-brand-red transition-colors">
                                        Enquire <ArrowUpRight size={16} />
                                    </span>
                                </div>
                            </motion.a>
                        ))}
                        <div className="border-t border-ink/15" />
                    </div>
                </div>
            </section>

            <ContactCTA />
        </main>
    );
}
